'use client';

import { Search, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {  useSearchParams } from "next/navigation";
import clsx from "clsx";
import isExternal from "@/lib/is-external";
import { getPathname, Link, usePathname, useRouter } from "@/src/i18n/navigation";
import { useLocale } from "next-intl";

interface Menu {
  id: string;
  text: string;
  link: string;
  content_id: string;
  children: Menu[];
}

interface SidebarMobileProps {
  show: boolean;
  onClose: () => void;
  menu: Menu[];
}

const navItem = {
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  hidden: {
    y: 50,
    opacity: 0,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
};

const navContainer = {
  visible: {
    //x: 0,
    opacity: 1,
    transition: {
      x: { velocity: 100 },
      duration: 0.3,
    },
  },
  hidden: {
    //x: -250,
    opacity: 0,
    transition: {
      x: { velocity: 100 },
      duration: 0.3,
    },
  },
};

const navList = {
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.07,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

export default function SidebarMobile(props: SidebarMobileProps) {
  const qs = useSearchParams();
  const router = useRouter();
  const lang = useLocale()
  const pathname = usePathname();

  const menu = props.menu
    .map((item) => (item.children.length > 0 ? item.children : item))
    .flat(4);

  const onPrefetchLanguage = () => {
    if (!qs) return;
    router.prefetch(`${pathname}?${qs.toString()}`, {
      locale: lang === "en" ? "id-ID" : "en",
    });
  };

  return (
    <AnimatePresence>
      {props.show && (
        <motion.div
          variants={navContainer}
          initial="hidden"
          animate={props.show ? "visible" : "hidden"}
          exit="hidden"
          className="bg-darkgold px-8 fixed top-0 left-0 w-screen h-screen z-[51] pt-5 flex flex-col"
        >
          <button onClick={props.onClose} className="cursor-pointer">
            <X className=" text-gold" />
          </button>

          <div className="flex pb-3  border-b border-b-gold w-full mt-10">
            <input
              type="text"
              className="text-white font-karbon placeholder:text-white flex-1 text-lg focus:outline-none"
              placeholder="Search"
              onKeyDown={(e) => {
                if (!qs) return;
                if (e.key === "Enter") {
                  const searchParam = new URLSearchParams(qs);
                  if (e.currentTarget.value) {
                    searchParam.set("q", e.currentTarget.value);
                  } else {
                    searchParam.delete("q");
                  }

                  if (pathname === "/search") {
                    router.push(`/search?${searchParam.toString()}`, {locale: lang});
                  } else {
                    const pathname = getPathname({
                      href: {
                        pathname :"/search",
                         query :  Object.fromEntries(searchParam.entries())
                      },
                      locale: lang,
                    })
                    window.open(pathname, "_blank");
                  }
                  props.onClose();
                }
              }}
            />
            <Search size={20} color="white" />
          </div>

          <motion.nav
            variants={navList}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="mt-16 flex flex-col gap-y-2"
          >
            {menu.map((item, index) => {
              const link = `${item.link}?${new URLSearchParams(qs).toString()}`;

              return (
                <motion.div variants={navItem} key={index}>
                  <Link
                    prefetch={!isExternal(link)}
                    href={link}
                    target={isExternal(link) ? "_blank" : "_self"}
                    onClick={props.onClose}
                    className="text-white font-karbon text-lg py-3"
                  >
                    {item.text}
                  </Link>
                </motion.div>
              );
            })}
          </motion.nav>

          <div className="flex justify-between mb-8 mt-auto">
            <div className="inline-flex items-center">
              <Link
                href={`${pathname}?${qs?.toString()}`}
                locale="en"
                onMouseEnter={onPrefetchLanguage}
                className={clsx("text-white font-karbon", {
                  "font-medium": lang === "en",
                })}
              >
                ENG
              </Link>
              <span className="text-white mx-1">|</span>
              <Link
                href={`${pathname}?${qs?.toString()}`}
                locale="id-ID"
                onMouseEnter={onPrefetchLanguage}
                className={clsx("text-white font-karbon", {
                  "font-medium": lang === "id-ID",
                })}
              >
                IND
              </Link>
            </div>

            <button
              onClick={() => {
                router.push("/login");
                props.onClose();
              }}
              className="text-white font-karbon"
            >
              Login
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
